import { ArrowLeft, LayoutDashboard, LogOut } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { logout } from "../../lib/api";
import { buildRouteContextLine, getRouteOwnershipContext } from "../../lib/routeOwnershipRegistry";

interface FormPortalHeaderProps {
  brandHref?: string;
  publicBackHref?: string;
  publicBackLabel?: string;
  brandName?: string;
  brandSubtitle?: string;
  brandLogoAlt?: string;
  brandLogoSrc?: string;
  brandLogoSrcSet?: string;
  adminDirectoryHref?: string;
  adminDirectoryLabel?: string;
  showAdminDirectoryLink?: boolean;
  showPublicBackLink?: boolean;
}

export function FormPortalHeader({
  brandHref = "/",
  publicBackHref = "/",
  publicBackLabel = "Forms",
  brandName = "Kreatethedate",
  brandSubtitle = "Forms Portal",
  brandLogoAlt = "Kreatethedate Logo",
  brandLogoSrc = "/assets/ktd-logo-header.png",
  brandLogoSrcSet = "/assets/ktd-logo-header.png 1x, /assets/ktd-logo-header@2x.png 2x",
  adminDirectoryHref = "/admin/forms-directory",
  adminDirectoryLabel = "Admin Directory",
  showAdminDirectoryLink = true,
  showPublicBackLink = true,
}: FormPortalHeaderProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";
  const isAdmin = location.pathname.startsWith("/admin");
  const isAdminDirectory = location.pathname === "/admin/forms-directory";
  const publicBackIsExternal = isExternalUrl(publicBackHref);
  const routeContext = getRouteOwnershipContext(location.pathname);
  const routeContextLine = buildRouteContextLine(location.pathname);
  const resolvedAdminDirectoryHref =
    adminDirectoryHref === "/admin/forms-directory" ? routeContext.returnRoute : adminDirectoryHref;
  const resolvedAdminDirectoryLabel = adminDirectoryLabel === "Admin Directory" ? routeContext.navigationLabel : adminDirectoryLabel;

  const brandContent = (
    <>
      <img
        src={brandLogoSrc}
        srcSet={brandLogoSrcSet}
        alt={brandLogoAlt}
        width={60}
        height={60}
        className="block h-12 w-12 shrink-0 object-contain [image-rendering:auto] sm:h-[60px] sm:w-[60px]"
        draggable={false}
      />
      <span className="leading-tight">
        <span className="block font-display text-xl font-bold">{brandName}</span>
        <span className="block text-xs font-bold uppercase tracking-[0.22em] text-[#FF6A2B]">{brandSubtitle}</span>
        <span className="mt-1 block text-[10px] font-black uppercase tracking-[0.18em] text-[#FF6A2B]/80">
          {routeContextLine}
        </span>
      </span>
    </>
  );

  const brandLink = isExternalUrl(brandHref) ? (
    <a href={brandHref} className="focus-ring inline-flex max-w-full items-center gap-3 rounded-lg">
      {brandContent}
    </a>
  ) : (
    <Link to={brandHref} className="focus-ring inline-flex max-w-full items-center gap-3 rounded-lg">
      {brandContent}
    </Link>
  );

  const publicBackLink = publicBackIsExternal ? (
    <a
      href={publicBackHref}
      className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[#FF6A2B]/35 px-4 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#fff9ef]/10"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      {publicBackLabel}
    </a>
  ) : (
    <Link
      to={publicBackHref}
      className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[#FF6A2B]/35 px-4 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#fff9ef]/10"
    >
      <ArrowLeft className="size-4" aria-hidden="true" />
      {publicBackLabel}
    </Link>
  );

  async function handleAdminLogout() {
    try {
      await logout();
    } finally {
      localStorage.removeItem("ktd_demo_auth");
      navigate("/admin/login", { replace: true });
    }
  }

  return (
    <header className="sticky top-0 z-40 border-b border-[#FF6A2B]/20 bg-[#050505] px-4 py-4 text-[#fff9ef] shadow-[0_18px_45px_rgba(5,5,5,.18)] sm:px-6 lg:px-8">
      <div className="mx-auto flex max-w-7xl flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        {brandLink}
        {isAdmin ? (
          <nav className="flex w-full flex-wrap items-center justify-start gap-2 sm:w-auto sm:justify-end">
            {!isAdminDirectory && showAdminDirectoryLink ? (
              <Link
                to={resolvedAdminDirectoryHref}
                className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[#FF6A2B]/35 px-3 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#fff9ef]/10 sm:px-4"
              >
                <LayoutDashboard className="size-4" aria-hidden="true" />
                {resolvedAdminDirectoryLabel}
              </Link>
            ) : null}
            {showPublicBackLink ? (
              <Link
                to={publicBackHref}
                className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[#FF6A2B]/35 px-3 text-sm font-extrabold text-[#FF6A2B] transition hover:bg-[#fff9ef]/10 sm:px-4"
              >
                <ArrowLeft className="size-4" aria-hidden="true" />
                {publicBackLabel}
              </Link>
            ) : null}
            <button
              type="button"
              onClick={() => void handleAdminLogout()}
              className="focus-ring inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#FF6A2B] px-3 text-sm font-extrabold text-[#050505] transition hover:bg-[#FF6A2B] sm:px-4"
            >
              <LogOut className="size-4" aria-hidden="true" />
              Logout
            </button>
          </nav>
        ) : !isHome ? (
          <div className="w-full sm:w-auto">{publicBackLink}</div>
        ) : null}
      </div>
    </header>
  );
}

function isExternalUrl(value: string) {
  return /^https?:\/\//i.test(value);
}
